import React, { useState, useEffect } from 'react';
import VideoShowcase from '../components/VideoShowcase';
import './BilateralMasteryPage.css';

export default function BilateralMasteryPage({ onNavigate }) {
  // Interactive State
  const [activeAgeBand, setActiveAgeBand] = useState('12-14');
  const [activeWengerStep, setActiveWengerStep] = useState(1);
  const [kickVelocityValue, setKickVelocityValue] = useState(65); // 0 to 100 slider
  const [passingVisionMode, setPassingVisionMode] = useState('360'); // '180' or '360'
  const [selectedPassTarget, setSelectedPassTarget] = useState(2); // ID of target

  // Scroll Intersection Observer: Highlight/Unhighlight cards as user scrolls
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-15% 0px -20% 0px',
      threshold: 0.25
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view-highlight');
        } else {
          entry.target.classList.remove('in-view-highlight');
        }
      });
    }, observerOptions);

    const cards = document.querySelectorAll(
      '.bm-metric-card, .bm-simulator-card, .bm-arc-visualizer, .bm-table-wrapper, .bm-premium-banner, .bm-age-card, .wenger-step-card, .principle-card'
    );

    cards.forEach((card) => observer.observe(card));

    return () => {
      cards.forEach((card) => observer.unobserve(card));
    };
  }, []);

  // Age band data from Table 3 in the paper
  const ageData = {
    '10-12': { recDom: '74.28%', recNon: '25.72%', relDom: '84.78%', relNon: '15.22%' },
    '12-14': { recDom: '74.29%', recNon: '25.71%', relDom: '83.66%', relNon: '16.35%' },
    '14-16': { recDom: '73.95%', recNon: '26.05%', relDom: '82.84%', relNon: '17.16%' },
    '16-18': { recDom: '73.62%', recNon: '26.39%', relDom: '82.57%', relNon: '17.43%' }
  };

  const currentAgeData = ageData[activeAgeBand] || ageData['12-14'];

  const wengerSteps = [
    {
      age: '5–10',
      title: 'EXPOSURE',
      desc: 'Early exposure to bilateral touches through fun-based drills and playful motor learning.'
    },
    {
      age: '11–14',
      title: 'THE BASEMENT',
      desc: 'Deep relationship with the ball. Feet must learn to do what the brain wants. Focus on "me and the ball" before neuroplasticity declines.'
    },
    {
      age: '14–17',
      title: 'FIRST FLOOR',
      desc: 'Introduction of physical talent, explosive speed, and first-floor athletic development.'
    },
    {
      age: '17–19',
      title: 'SECOND FLOOR',
      desc: 'Tactical intelligence and second-floor decision-making with teammates under elite press.'
    }
  ];

  // Pass Targets for 360 Radar Visualizer
  const passTargets = [
    { id: 1, label: 'Forward Pivot', angle: 0, distance: '85px', x: 0, y: -75, isBackFoot: false, desc: 'Direct vertical penetration' },
    { id: 2, label: 'Right Wing Switch', angle: 60, distance: '90px', x: 75, y: -45, isBackFoot: false, desc: 'Dominant side release' },
    { id: 3, label: 'Overlapping Fullback', angle: 120, distance: '80px', x: 70, y: 40, isBackFoot: true, desc: 'Requires weak-foot open body shape' },
    { id: 4, label: 'Center Back Safety', angle: 180, distance: '75px', x: 0, y: 75, isBackFoot: true, desc: 'Locked out for 180° right-footed players' },
    { id: 5, label: 'Diagonal Weak Switch', angle: 240, distance: '90px', x: -70, y: 40, isBackFoot: true, desc: 'Bypasses 2 defensive press lines' },
    { id: 6, label: 'Left Wing Channel', angle: 300, distance: '85px', x: -75, y: -45, isBackFoot: true, desc: 'Instant weak-foot diagonal pass' },
  ];

  // Calculate dynamic kicking velocity values based on slider
  const domVel = (12.0 + (kickVelocityValue / 100) * 8.0).toFixed(2); // m/s
  const nonDomVel = (domVel * 0.832).toFixed(2); // 16.8% asymmetry index
  const gapKm = ((domVel - nonDomVel) * 3.6).toFixed(1); // km/h difference

  const activeTargetObj = passTargets.find(t => t.id === selectedPassTarget) || passTargets[0];

  return (
    <div className="bilateral-mastery-container">
      
      {/* ── HERO BANNER SECTION ────────────────────────────────────────── */}
      <section className="bm-hero">
        <div className="bm-hero-overlay"></div>
        <div className="container bm-hero-content">
          <div className="bm-badge">OPINION PAPER • PLAYERMAKER PRO</div>
          <h1 className="bm-hero-title">
            CLOSING <span className="highlight-pink">THE GAP:</span><br />
            BILATERAL FOOTBALL MASTERY IN THE MODERN GAME
          </h1>
          <p className="bm-hero-subtitle">
            A comprehensive research report on how measuring and training footedness transforms youth player development across 30,000+ tracking sessions.
          </p>
          <div className="bm-hero-meta">
            <span>By Dr. Erez Morag, Dr. Steve Barrett, Dr. Eran Amit, Niv Levi</span>
            <span className="meta-divider">•</span>
            <span>Data Collection 2023–2026</span>
          </div>

          <div className="bm-hero-actions">
            <a href="#abstract" className="bm-btn primary">Explore Research Data</a>
            <button onClick={() => onNavigate('/faq')} className="bm-btn outline">Setup FAQ</button>
          </div>
        </div>
      </section>

      {/* ── KEY METRICS OVERVIEW (CARDS) ───────────────────────────────── */}
      <section id="abstract" className="bm-section">
        <div className="container">
          <div className="section-header center">
            <div className="sub-title">EXECUTIVE SUMMARY</div>
            <h2 className="title">Footedness is measurable, trainable, and under-addressed.</h2>
            <p className="desc">
              Across more than 30,000 youth players, a 16.8% asymmetry in maximum kicking speeds between dominant and non-dominant feet was observed. In the modern game, this gap determines tactical fluidity.
            </p>
          </div>

          <div className="bm-metrics-grid">
            <div className="bm-metric-card glow-pink">
              <div className="metric-val">16.8%</div>
              <div className="metric-title">ASYMMETRY INDEX</div>
              <p className="metric-desc">Max release velocity gap between dominant and non-dominant foot.</p>
            </div>

            <div className="bm-metric-card glow-teal">
              <div className="metric-val">18%</div>
              <div className="metric-title">TWO-FOOTED PLAYERS</div>
              <p className="metric-desc">Share of elite top-five-league players classified as bilateral in match play.</p>
            </div>

            <div className="bm-metric-card glow-pink">
              <div className="metric-val">14–15%</div>
              <div className="metric-title">AMBIDEXTERITY PREMIUM</div>
              <p className="metric-desc">Salary uplift for two-footed professionals over one-footed peers.</p>
            </div>

            <div className="bm-metric-card glow-teal">
              <div className="metric-val">7–14</div>
              <div className="metric-title">CRITICAL WINDOW</div>
              <p className="metric-desc">Wenger's technical window to embed bilateral motor foundations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── INTERACTIVE KICKING PHYSICS SIMULATOR ────────────────────────── */}
      <section className="bm-section dark-bg">
        <div className="container">
          <div className="section-header">
            <div className="sub-title">03 / THE PHYSICS OF THE KICK</div>
            <h2 className="title">Dominant vs Non-Dominant Release Velocity</h2>
            <p className="desc">
              Adjust the power slider to simulate shot effort and observe the 16.8% asymmetry gap between dominant and weak-foot release speed.
            </p>
          </div>

          <div className="bm-simulator-card card-tech">
            <div className="sim-controls">
              <label htmlFor="power-slider" className="sim-label">
                Simulated Shot Effort: <strong>{kickVelocityValue}%</strong>
              </label>
              <input 
                id="power-slider"
                type="range" 
                min="20" 
                max="100" 
                value={kickVelocityValue}
                onChange={(e) => setKickVelocityValue(Number(e.target.value))}
                className="sim-slider"
              />
            </div>

            <div className="sim-results-grid">
              <div className="sim-foot-box dominant">
                <div className="foot-badge">DOMINANT FOOT</div>
                <div className="foot-speed">{domVel} <span className="unit">m/s</span></div>
                <div className="foot-km">({(domVel * 3.6).toFixed(1)} km/h)</div>
                <div className="power-bar-bg">
                  <div className="power-bar-fill dom" style={{ width: `${Math.min(100, (domVel / 20) * 100)}%` }}></div>
                </div>
              </div>

              <div className="sim-vs-circle">VS</div>

              <div className="sim-foot-box non-dominant">
                <div className="foot-badge weak">NON-DOMINANT FOOT</div>
                <div className="foot-speed">{nonDomVel} <span className="unit">m/s</span></div>
                <div className="foot-km">({(nonDomVel * 3.6).toFixed(1)} km/h)</div>
                <div className="power-bar-bg">
                  <div className="power-bar-fill non" style={{ width: `${Math.min(100, (nonDomVel / 20) * 100)}%` }}></div>
                </div>
              </div>
            </div>

            <div className="sim-footer">
              <span>Measured Velocity Gap: <strong>{gapKm} km/h</strong> (16.8% Asymmetry)</span>
              <span className="sim-note">Data sample: 30,000+ youth players (10–18 years old) using Playermaker foot sensors.</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── VIDEO INTERACTIVE MEDIA CLIPS ──────────────────────────────── */}
      <section className="bm-section">
        <div className="container">
          <div className="section-header center">
            <div className="sub-title">REAL-WORLD MOTION TRACKING</div>
            <h2 className="title">Foot-Mounted Sensors in Action</h2>
          </div>
          <VideoShowcase 
            src="https://cdn.shopify.com/videos/c/o/v/0d8845e6bf9c4be8af8162d1263b7a8e.mp4" 
            showText={true}
          />
        </div>
      </section>

      {/* ── TECHNICAL ACTIONS: 360° PASSING & FIRST TOUCH ────────────────── */}
      <section className="bm-section dark-bg">
        <div className="container">
          <div className="section-header center">
            <div className="sub-title">04 / TECHNICAL ACTIONS &amp; VISION</div>
            <h2 className="title">Two feet open <span className="highlight-pink">360°</span> of passing.</h2>
          </div>

          <div className="bm-split-grid">
            
            <div className="bm-split-text">
              <div className="bm-mode-switcher">
                <button 
                  className={`mode-btn ${passingVisionMode === '180' ? 'active pink' : ''}`}
                  onClick={() => setPassingVisionMode('180')}
                >
                  180° One-Footed Arc
                </button>
                <button 
                  className={`mode-btn ${passingVisionMode === '360' ? 'active teal' : ''}`}
                  onClick={() => setPassingVisionMode('360')}
                >
                  360° Ambidextrous Radar
                </button>
              </div>

              <div className="bm-feature-block">
                <h3>PASSING ARCS</h3>
                <p>
                  Right-footed players attempt roughly 87% of their passes with their dominant foot, limiting their field of vision to 180 degrees. Ambidextrous players expand passing angles by 180 degrees, unlocking passing lanes physically unavailable to one-footed peers.
                </p>
              </div>

              <div className="bm-feature-block">
                <h3>BACK-FOOT FIRST TOUCH</h3>
                <p>
                  Elite frameworks (Total Football, Game Intelligence) emphasize receiving on the "back foot", furthest from ball origin. If a player can only receive with their dominant foot, they turn into pressure. Bilateral players receive cleanly and break high pressing lines instantly.
                </p>
              </div>
            </div>

            {/* DYNAMIC ANIMATED RADAR VISUALIZER */}
            <div className="bm-arc-visualizer card-tech">
              <div className="radar-header">
                <div className="radar-status">
                  <span className={`status-dot ${passingVisionMode === '360' ? 'active' : 'restricted'}`}></span>
                  VISION FIELD: <strong>{passingVisionMode === '360' ? 'FULL 360° UNLOCKED' : 'RESTRICTED 180°'}</strong>
                </div>
              </div>

              <div className="radar-pitch">
                {/* Rotating Sweeper Beam */}
                <div className={`radar-beam ${passingVisionMode === '180' ? 'sweep-180' : 'sweep-360'}`}></div>

                {/* Field Markings */}
                <div className="radar-ring inner"></div>
                <div className="radar-ring outer"></div>
                <div className="radar-line-v"></div>
                <div className="radar-line-h"></div>

                {/* Central Player Dot */}
                <div className="radar-player-center">
                  <div className="player-pulse"></div>
                  <div className="player-core"></div>
                </div>

                {/* Interactive Pass Target Nodes */}
                {passTargets.map((target) => {
                  const isDisabledIn180 = passingVisionMode === '180' && target.isBackFoot;
                  const isSelected = selectedPassTarget === target.id;

                  return (
                    <button
                      key={target.id}
                      className={`target-node ${isDisabledIn180 ? 'disabled' : 'active'} ${isSelected ? 'selected' : ''}`}
                      style={{
                        transform: `translate(${target.x}px, ${target.y}px)`
                      }}
                      onClick={() => {
                        if (!isDisabledIn180) setSelectedPassTarget(target.id);
                      }}
                      title={isDisabledIn180 ? 'Target Locked: Requires Weak-Foot Ambidexterity' : target.label}
                    >
                      <span className="target-pulse"></span>
                      <span className="target-icon">{isDisabledIn180 ? '🔒' : '⚽'}</span>
                    </button>
                  );
                })}

                {/* Pass Trajectory Line SVG */}
                <svg className="pass-trajectory-svg" viewBox="-120 -120 240 240">
                  <line 
                    x1="0" 
                    y1="0" 
                    x2={activeTargetObj.x} 
                    y2={activeTargetObj.y} 
                    className="pass-line"
                  />
                  <circle 
                    cx={activeTargetObj.x} 
                    cy={activeTargetObj.y} 
                    r="4" 
                    className="pass-impact-dot"
                  />
                </svg>
              </div>

              <div className="radar-target-details">
                <div className="target-title">
                  <span>Selected Option: <strong>{activeTargetObj.label}</strong></span>
                  <span className="target-angle">{activeTargetObj.angle}° Angle</span>
                </div>
                <p className="target-desc">{activeTargetObj.desc}</p>
                {passingVisionMode === '180' && activeTargetObj.isBackFoot && (
                  <div className="target-warning">
                    ⚠️ Option locked for one-footed players! Requires weak-foot bilateral touch.
                  </div>
                )}
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* ── TACTICAL ADVANTAGE COMPARISON TABLE ───────────────────────────── */}
      <section className="bm-section">
        <div className="container">
          <div className="section-header center">
            <div className="sub-title">05 / TACTICAL ADVANTAGE</div>
            <h2 className="title">Bilateralism Beats the Press</h2>
          </div>

          <div className="bm-table-wrapper card-tech">
            <table className="bm-table">
              <thead>
                <tr>
                  <th>Tactical Concept</th>
                  <th>One-Footed Player</th>
                  <th className="highlight-col">Two-Footed Player</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Press Resistance</strong></td>
                  <td>Vulnerable to "Shadow Marking"</td>
                  <td className="highlight-col"><strong>High; turns in either direction</strong></td>
                </tr>
                <tr>
                  <td><strong>Passing Arc</strong></td>
                  <td>Limited (~180 degrees)</td>
                  <td className="highlight-col"><strong>Full (~360 degrees)</strong></td>
                </tr>
                <tr>
                  <td><strong>Transition Speed</strong></td>
                  <td>Slower (extra reorganization touches)</td>
                  <td className="highlight-col"><strong>Rapid (direct release on back foot)</strong></td>
                </tr>
                <tr>
                  <td><strong>Unpredictability</strong></td>
                  <td>Low; predictable body shape</td>
                  <td className="highlight-col"><strong>High; keeps defenders guessing</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bm-premium-banner">
            <div className="premium-val">14–15%</div>
            <div className="premium-text">
              <h3>THE AMBIDEXTERITY SALARY PREMIUM</h3>
              <p>Research across Europe's top leagues confirms two-footed professionals command a 14–15% salary premium over one-footed peers due to tactical versatility.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── DEVELOPMENTAL DATA BY AGE BAND ──────────────────────────────── */}
      <section className="bm-section dark-bg">
        <div className="container">
          <div className="section-header center">
            <div className="sub-title">07 / DEVELOPMENTAL DATA</div>
            <h2 className="title">Footedness Performance Indicators Across Age Bands</h2>
            <p className="desc">Click through age bands to analyze non-dominant foot usage progression in 30,000+ youth players.</p>
          </div>

          <div className="bm-age-tabs">
            {Object.keys(ageData).map((band) => (
              <button 
                key={band} 
                className={`age-tab-btn ${activeAgeBand === band ? 'active' : ''}`}
                onClick={() => setActiveAgeBand(band)}
              >
                Ages {band}
              </button>
            ))}
          </div>

          <div className="bm-age-card card-tech">
            <h3 className="age-card-title">Foot Usage Telemetry for Ages {activeAgeBand}</h3>
            
            <div className="age-grid">
              <div className="age-stat-box">
                <div className="stat-lbl">Receive (Dominant)</div>
                <div className="stat-num pink">{currentAgeData.recDom}</div>
              </div>

              <div className="age-stat-box">
                <div className="stat-lbl">Receive (Non-Dominant)</div>
                <div className="stat-num teal">{currentAgeData.recNon}</div>
              </div>

              <div className="age-stat-box">
                <div className="stat-lbl">Release (Dominant)</div>
                <div className="stat-num pink">{currentAgeData.relDom}</div>
              </div>

              <div className="age-stat-box">
                <div className="stat-lbl">Release (Non-Dominant)</div>
                <div className="stat-num teal">{currentAgeData.relNon}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WENGER'S CRITICAL WINDOW TIMELINE ────────────────────────────── */}
      <section className="bm-section">
        <div className="container">
          <div className="section-header">
            <div className="sub-title">08 / THE TECHNICAL BASEMENT</div>
            <h2 className="title">Wenger's Critical Window (Ages 7–14)</h2>
            <p className="desc">
              Arsène Wenger's structural blueprint: "Technique is the basement." If a player does not establish two-footed proficiency by age 14, elite potential is severely restricted.
            </p>
          </div>

          <div className="wenger-timeline">
            {wengerSteps.map((step, idx) => (
              <div 
                key={idx} 
                className={`wenger-step-card ${activeWengerStep === idx ? 'active' : ''}`}
                onClick={() => setActiveWengerStep(idx)}
              >
                <div className="wenger-age-tag">Ages {step.age}</div>
                <h4 className="wenger-title">{step.title}</h4>
                <p className="wenger-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5 PRACTICAL STRATEGIES ───────────────────────────────────────── */}
      <section className="bm-section dark-bg">
        <div className="container">
          <div className="section-header center">
            <div className="sub-title">09 / PRACTICAL STRATEGIES</div>
            <h2 className="title">Five Principles for Weak-Foot Development</h2>
          </div>

          <div className="bm-principles-grid">
            <div className="principle-card">
              <div className="p-num">01</div>
              <h4>Integrate, Don't Isolate</h4>
              <p>Weak-foot touches should be integrated into every drill rather than separated into isolated blocks.</p>
            </div>

            <div className="principle-card">
              <div className="p-num">02</div>
              <h4>Gamification</h4>
              <p>Creating constraints like "double points for weak-foot goals" forces players to experiment under pressure.</p>
            </div>

            <div className="principle-card">
              <div className="p-num">03</div>
              <h4>The "Wall" Method</h4>
              <p>High-repetition rebounder wall training builds neural pathways and muscle memory rapidly.</p>
            </div>

            <div className="principle-card">
              <div className="p-num">04</div>
              <h4>Game-Based Learning</h4>
              <p>Rondos and small-sided games with weak-foot rules simulate match intensity and decision speed.</p>
            </div>

            <div className="principle-card full-width">
              <div className="p-num">05</div>
              <h4>Objective Tracking with Playermaker</h4>
              <p>Foot-mounted sensors empower coaches and players to move from subjective opinion to data-driven accountability, tracking weak-foot release percentages and velocity shifts in real time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA SECTION ─────────────────────────────────────────────────── */}
      <section className="bm-cta-section">
        <div className="container center">
          <h2>Ready to Measure and Close the Weak-Foot Gap?</h2>
          <p>Equip your player or team with Playermaker 2.0. FIFA approved smart boot tracker with free Canadian shipping.</p>
          <div className="cta-buttons">
            <button onClick={() => onNavigate('/')} className="bm-btn primary">Order Playermaker 2.0</button>
            <button onClick={() => onNavigate('/faq')} className="bm-btn outline">View Support FAQ</button>
          </div>
        </div>
      </section>

    </div>
  );
}
